import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  useToast,
} from "@chakra-ui/react";
import { ResponsiveLine } from "@nivo/line";
import { useEffect, useState } from "react";
import LocalSpinner from "../../../../components/LocalSpinner";
import { H2 } from "../../../../components/text/headings";
import { useGetSuppliersYearlyData } from "../../../../hooks/query";
import { constructQueryParamsString, getFormattedNumValue, showErrorToast } from "../../../../utils";
import { ScopeDropdown, TrendCalculationMethodDropdown, YearStartEndDropdown } from "./SupplierTrends";
import * as React from "react";

const SupplierTrends = ({ supplierId }) => {
  const toast = useToast();

  const [startYear, setStartYear] = useState(2021);
  const [endYear, setEndYear] = useState(2030);
  const [scope, setScope] = useState("All Scopes");
  const [chartData, setChartData] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [method, setMethod] = useState("Emission");

  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  /**
   * Processes raw emission data and formats it for chart visualization based on selected scope and calculation method.
   * Creates three data series: actual emissions, targets, and projections.
   */
  useEffect(() => {
    if (rawData?.[0]?.yearlyEmissionData && scope) {
      /**
       * Extracts emission values based on the selected scope
       * @param {Object} data - Yearly emission data for a specific year
       * @returns {Object} Formatted emission values (actual and projected)
       */
      const getData = (data) => {
        let emission = 0;
        let intensity = data?.intensity || 0;
        let projectedEmission = 0;
        let projectedIntensity = data?.projectedIntensity || 0;

        // Calculate emissions based on scope selection
        if (scope === "All Scopes") {
          emission = data?.totalCo2Emissions || 0;
          projectedEmission = data?.projectedTotalEmissions || 0;
        } else if (scope === "Scope 1 and 2") {
          emission = (data?.scope1 || 0) + (data?.scope2 || 0);
          projectedEmission = (data?.projectedScope1Emissions || 0) + (data?.projectedScope2Emissions || 0);
        } else if (scope === "Scope 3") {
          emission = data?.scope3 || 0;
          projectedEmission = data?.projectedScope3Emissions || 0;
        }

        return {
          emission: emission,
          intensity: intensity,
          projectedEmissions: projectedEmission,
          projectedIntensity: projectedIntensity,
        };
      };

      const currentYear = new Date().getFullYear();

      const formattedData = [
        {
          id: "Supplier Emissions",
          data: Object.entries(rawData[0].yearlyEmissionData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? getData(values)?.intensity : getData(values)?.emission,
          })),
          color: "#63AD03",
        },
        {
          id: "Target",
          data: Object.entries(rawData[0].yearlyEmissionData).map(([year, values]) => ({
            x: year,
            y: method === "Intensity" ? values?.intensityTarget : values?.target,
          })),
          color: "#FF9500",
        },
        {
          id: "Projected",
          data: Object.entries(rawData[0].yearlyEmissionData)
            // only show projections from previous year onwards + 2 years
            .filter(([year]) => parseInt(year) >= currentYear - 1 && parseInt(year) < currentYear + 3)
            .map(([year, values]) => ({
              x: year,
              y: method === "Intensity" ? getData(values)?.projectedIntensity : getData(values)?.projectedEmissions,
            })),
          color: "#A6F334",
        },
      ];
      setChartData(formattedData);
    }
  }, [rawData, scope, method]);

  const getQueryParamsString = () => {
    if (supplierId && years?.length > 0) {
      const queryParams = {
        years: years,
        supplierId: supplierId,
        scope: scope,
      };
      return constructQueryParamsString(queryParams);
    }
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (response) => {
      setRawData(response);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
    const dashedIds = ["Target", "Projected"];

    return series.map(({ id, data, color }, index) => (
      <path
        key={id}
        d={lineGenerator(
          data.map((d) => ({
            x: xScale(d.data.x),
            y: yScale(d.data.y),
          }))
        )}
        fill="none"
        stroke={color}
        style={
          dashedIds.includes(id)
            ? {
                strokeDasharray: "3, 6",
                strokeWidth: 3,
              }
            : {
                strokeWidth: 1,
              }
        }
      />
    ));
  };

  return (
    <Box>

      <Accordion allowToggle>
        <AccordionItem>

          <AccordionButton>
            <H2 fontSize="body.2xl">Trends</H2>
            <AccordionIcon mr={0} ml="auto" />
          </AccordionButton>

          <AccordionPanel mt={4}>
            <Flex alignSelf="end" gap={2}>
              <YearStartEndDropdown
                setStartYear={setStartYear}
                startYear={startYear}
                setEndYear={setEndYear}
                endYear={endYear}
              />
              <ScopeDropdown setSelected={setScope} selected={scope} />
            </Flex>
            <Flex justifyContent="end">
              <TrendCalculationMethodDropdown setSelected={setMethod} selected={method} />
            </Flex>

            {isFetching && <LocalSpinner />}

            <Box height="500px" width="100%">
              {chartData && chartData.length > 0 && !isFetching && (
                <ResponsiveLine
                  data={chartData}
                  margin={{ top: 50, right: 50, bottom: 90, left: 120 }}
                  yScale={{ type: "linear", min: "auto", max: "auto", stacked: false, reverse: false }}
                  axisBottom={{ legend: "Time", legendOffset: 36 }}
                  axisLeft={{
                    legend: method === "Intensity" ? "Intensity (TCO2e/£m)" : "Total Emissions (TCO2e)",
                    legendOffset: -100,
                    legendPosition: "middle",
                  }}
                  enablePoints={true}
                  pointSize={10}
                  colors={(d) => d.color}
                  pointBorderWidth={2}
                  pointBorderColor={{ from: "seriesColor" }}
                  pointLabelYOffset={-12}
                  enableTouchCrosshair={true}
                  useMesh={true}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      translateY: 60,
                      itemWidth: 80,
                      itemHeight: 22,
                      symbolShape: "circle",
                    },
                  ]}
                  layers={[
                    // includes all default layers
                    "grid",
                    "markers",
                    "axes",
                    "areas",
                    "crosshair",
                    "line",
                    "slices",
                    "points",
                    "mesh",
                    "legends",
                    DashedSolidLine, // add the custome layer here
                  ]}
                  tooltip={({ point }) => (
                    <Box bg="white" p="8px" border="1px solid #ccc" borderRadius="4px">
                      <Text fontWeight="bold" color={point.color}>
                        {point.serieId}
                      </Text>
                      <Text>Year: {point.data.x}</Text>
                      <Text>Value: {getFormattedNumValue(Number(point.data.y), 2)}</Text>
                    </Box>
                  )}
                />
              )}
            </Box>
          </AccordionPanel>

        </AccordionItem>
      </Accordion>


    </Box>
  );
};

export default SupplierTrends;
